import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Btn, Card, Spinner, Input, Select, useToast } from '../components/shared/UI';
import { fmtCurrency, fmtDate } from '../utils/helpers';
import { useT } from '../i18n';
import api from '../utils/api';

const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const AGING_KEYS = ['current', '1-30', '31-60', '61-90', '90+'];

export default function ClientStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), 0, 1)));
  const [to, setTo]     = useState(iso(today));
  const [currency, setCurrency] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const query = useCallback(() => {
    const p = new URLSearchParams({ from, to });
    if (currency) p.set('currency', currency);
    return p.toString();
  }, [from, to, currency]);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    api.get(`/clients/${id}/statement?${query()}`)
      .then(r => setData(r.data))
      .catch(err => show(err.response?.data?.message || t('statement.loadFailed'), 'error'))
      .finally(() => setLoading(false));
  }, [id, query]); // show / t are stable enough; re-fetch only when the filters change

  const preset = kind => {
    const y = today.getFullYear(), m = today.getMonth();
    if (kind === 'thisMonth') { setFrom(iso(new Date(y, m, 1))); setTo(iso(today)); }
    if (kind === 'lastMonth') { setFrom(iso(new Date(y, m - 1, 1))); setTo(iso(new Date(y, m, 0))); }
    if (kind === 'thisYear')  { setFrom(iso(new Date(y, 0, 1))); setTo(iso(today)); }
  };

  const openPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/pdf/statement/${id}?${query()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { show(t('editor.pdfFailed'), 'error'); }
    finally { setPdfLoading(false); }
  };

  const money = n => fmtCurrency(n, data?.symbol || 'RM');

  return (
    <div>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button onClick={() => navigate('/clients')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, marginBottom: 8, fontFamily: 'inherit', padding: 0 }}>
            ← {t('statement.back')}
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)' }}>{t('statement.title')}</h1>
          {data?.client && <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>{data.client.name}{data.client.company ? ` · ${data.client.company}` : ''}</p>}
        </div>
        <Btn variant="primary" onClick={openPdf} disabled={pdfLoading || !data}>
          {pdfLoading ? t('editor.generating') : `🖨 ${t('statement.downloadPdf')}`}
        </Btn>
      </div>

      {/* Period */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Input label={t('statement.from')} type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={{ width: 160 }} />
          <Input label={t('statement.to')} type="date" value={to} min={from} onChange={e => setTo(e.target.value)} style={{ width: 160 }} />
          {data?.currencies?.length > 1 && (
            <Select label={t('statement.currency')} value={data.currency} onChange={e => setCurrency(e.target.value)} style={{ width: 110 }}>
              {data.currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['thisMonth', 'lastMonth', 'thisYear'].map(k => (
              <Btn key={k} size="sm" variant="ghost" onClick={() => preset(k)}>{t(`statement.${k}`)}</Btn>
            ))}
          </div>
        </div>
      </Card>

      {loading || !data ? <Spinner /> : <>
        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            { l: t('statement.opening'), v: data.openingBalance },
            { l: t('statement.invoiced'), v: data.totals.invoiced },
            { l: t('statement.paid'), v: data.totals.paid },
            { l: t('statement.credited'), v: data.totals.credited },
            { l: t('statement.balanceDue'), v: data.closingBalance, strong: true },
          ].map(x => (
            <Card key={x.l} style={{ padding: '14px 16px', ...(x.strong ? { border: '2px solid var(--accent)' } : {}) }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)' }}>{x.l}</div>
              <div style={{ fontSize: x.strong ? 20 : 17, fontWeight: 700, color: x.strong ? 'var(--heading)' : 'var(--text)', marginTop: 4 }}>{money(x.v)}</div>
            </Card>
          ))}
        </div>

        {/* Transactions */}
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>{t('statement.date')}</th>
                  <th style={th}>{t('statement.details')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('statement.invoiced')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('statement.paidCredit')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('statement.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'var(--hover-bg)', fontWeight: 600 }}>
                  <td style={td}>{fmtDate(data.from)}</td>
                  <td style={td}>{t('statement.opening')}</td>
                  <td style={td} /><td style={td} />
                  <td style={{ ...td, textAlign: 'right' }}>{money(data.openingBalance)}</td>
                </tr>
                {data.lines.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--text-subtle)', padding: 28 }}>{t('statement.empty')}</td></tr>
                )}
                {data.lines.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border-light)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fmtDate(l.date)}</td>
                    <td style={td}>
                      <span style={{ fontWeight: 600 }}>{t(`statement.k_${l.kind}`)}</span>{' '}
                      <button onClick={() => navigate(`/documents/${l.docId}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', padding: 0 }}>{l.ref}</button>
                      {l.kind === 'payment' && l.detail && <div style={sub}>{l.detail}</div>}
                      {l.kind === 'invoice' && l.dueDate && <div style={sub}>{t('statement.due', { date: fmtDate(l.dueDate) })}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{l.debit ? money(l.debit) : ''}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--success)' }}>{l.credit ? money(l.credit) : ''}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(l.balance)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--navy)', color: '#fff', fontWeight: 700 }}>
                  <td style={{ ...td, color: '#fff' }}>{fmtDate(data.to)}</td>
                  <td style={{ ...td, color: '#fff' }}>{t('statement.closing')}</td>
                  <td style={{ ...td, color: '#fff', textAlign: 'right' }}>{money(data.totals.invoiced)}</td>
                  <td style={{ ...td, color: '#fff', textAlign: 'right' }}>{money(data.totals.paid + data.totals.credited)}</td>
                  <td style={{ ...td, color: '#fff', textAlign: 'right' }}>{money(data.closingBalance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* Aging */}
        <Card>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)', marginBottom: 10 }}>{t('statement.aging')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 110px), 1fr))', gap: 10 }}>
            {AGING_KEYS.map(k => (
              <div key={k} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  {k === 'current' ? t('statement.current') : t('statement.days', { range: k })}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3, color: k !== 'current' && data.aging[k] > 0 ? 'var(--danger)' : 'var(--text)' }}>{money(data.aging[k])}</div>
              </div>
            ))}
          </div>
        </Card>
      </>}
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '11px 16px', fontSize: 13, color: 'var(--text)', verticalAlign: 'top' };
const sub = { fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 };
